/**
 * Date & time helpers — everything displayed to the user should render in IST
 * with a single, predictable format. Storage and API payloads stay ISO/UTC.
 *
 *   formatDate(x)      → "dd/mm/yyyy"                (empty string on falsy)
 *   formatDateTime(x)  → "dd/mm/yyyy hh:mm AM/PM"
 *
 * Accepted inputs: ISO string ("2026-08-06T10:33:00Z"),
 *                  date-only string ("2026-08-06"),
 *                  Date instance, epoch ms (number).
 */
const IST = "Asia/Kolkata";

function toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === "string") {
    // Bare "YYYY-MM-DD" is treated as UTC midnight → cast to a date-only
    // display without any timezone drift.
    const bare = /^\d{4}-\d{2}-\d{2}$/.test(input);
    const d = new Date(bare ? `${input}T00:00:00+05:30` : input);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const dateParts = (d) => {
  const out = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST, day: "2-digit", month: "2-digit", year: "numeric",
  }).formatToParts(d).reduce((a, p) => (a[p.type] = p.value, a), {});
  return `${out.day}/${out.month}/${out.year}`;
};

const timeParts = (d) => {
  const out = new Intl.DateTimeFormat("en-US", {
    timeZone: IST, hour: "2-digit", minute: "2-digit", hour12: true,
  }).formatToParts(d).reduce((a, p) => (a[p.type] = p.value, a), {});
  // en-US emits {hour:"09", minute:"05", dayPeriod:"AM"}
  const hh = out.hour.padStart(2, "0");
  return `${hh}:${out.minute} ${out.dayPeriod}`;
};

export function formatDate(input) {
  const d = toDate(input);
  return d ? dateParts(d) : "";
}

export function formatDateTime(input) {
  const d = toDate(input);
  return d ? `${dateParts(d)} ${timeParts(d)}` : "";
}

/** ISO yyyy-mm-dd for today in IST — safe default for <input type="date">. */
export function todayISO() {
  const out = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  return out; // en-CA gives yyyy-mm-dd
}
